import React, { useEffect, useState, ChangeEvent, SyntheticEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { NavLink, useNavigate } from 'react-router-dom';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import Title from '../../../components/Title';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { Type_Country } from '../../../models/type_country';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const BancoAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [name, setName] = useState('');
  const [typeCountryId, setTypeCountryId] = useState('');
  const [typeCountryList, setTypeCountryList] = useState<Type_Country[]>([]);
  const [isModified, setIsModified] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        showLoading();
        const { data } = await axios.get('typecountry');
        setTypeCountryList(data.data);
      } catch (error) {
        console.error('Error al cargar países:', error);
        showAlert('Error al cargar países', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchCountries();
  }, []);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!name.trim() || !typeCountryId) {
      showAlert('Nombre y país son obligatorios', 'warning');
      return;
    }

    try {
      showLoading();
      await axios.post('banks', {
        name,
        type_country_id: Number(typeCountryId),
      });
      showAlert('Banco creado exitosamente', 'success');
      navigate('/administrar/bancos');
    } catch (error: any) {
      console.error('Error al crear banco:', error);
      if (error.response?.data?.message) {
        showAlert(error.response.data.message, 'error');
      } else {
        showAlert('Error al crear el banco', 'error');
      }
    } finally {
      hideLoading();
    }
  };

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Crear Banco</Title>
      </Box>

      <Card>
        <Divider />
        <form onSubmit={handleSubmit}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre del Banco"
                  value={name}
                  size="small"
                  id="name"
                  onChange={(e) => {
                    setName(e.target.value);
                    setIsModified(true);
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>País</InputLabel>
                  <Select
                    size="small"
                    labelId="typeCountryId"
                    id="typeCountryId"
                    value={typeCountryId}
                    onChange={(e) => {
                      setTypeCountryId(e.target.value);
                      setIsModified(true);
                    }}
                    label="País"
                  >
                    {typeCountryList.map((tc) => (
                      <MenuItem key={tc.id} value={tc.id.toString()}>
                        {tc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>

          <Divider />
          <CardActions>
            <Button
              size='small'
              type="submit"
              variant="contained"
              disabled={!isModified}
            >
              Guardar
            </Button>
            <Button
              size='small'
              color="secondary"
              variant="outlined"
              onClick={() => navigate('/administrar/bancos')}
            >
              Cancelar
            </Button>
          </CardActions>
        </form>
      </Card>
    </HeaderResponsive>
  );
};

export default BancoAdd;
